import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Checkbox } from "@progress/kendo-react-inputs";
import { Form, Field, FormElement } from "@progress/kendo-react-form";

import {
    FormDropDownList,
    FormAutoComplete,
} from "../../forms/form-components";

export const AdministratorMyShortedUnbroadcastedAvailableEvents = () => {
    const dispatch = useDispatch();

    const formReference = useRef(null);

    const defaultTicketingWebsite = {
        ticketingWebsiteId: "",
        name: "All Websites",
    };

    const [myShortedUnbroadcastedAvailableEvents, setMyShortedUnbroadcastedAvailableEvents] = useState(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [totalEventCount, setTotalEventCount] = useState(0);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [lastSelectedTicketGroupId, setLastSelectedTicketGroupId] = useState(undefined);
    const [ticketingWebsites, setTicketingWebsites] = useState([]);
    const [venues, setVenues] = useState([]);
    const [venueName, setVenueName] = useState(undefined);
    const [areVenuesLoading, setAreVenuesLoading] = useState(false);
    const [searchCriteria, setSearchCriteria] = useState(undefined);

    const initialPageState = {
        skip: 0,
        take: 20,
    };
    const [page, setPage] = useState(initialPageState);
    const pageChange = (event) => {
        setPage(event.page);
    };

    const [showStatuses, setShowStatuses] = useState(false);
    const [statuses, setStatuses] = useState(undefined);

    const autocompleteTimeout = useRef();
    const timeoutDelay = 500; //Milliseconds

    const getData = (dataItem) => {
        if (dataItem.ticketingWebsite !== undefined
            && dataItem.ticketingWebsite.value !== "") {
            dataItem.ticketingWebsiteId = parseInt(dataItem.ticketingWebsite.ticketingWebsiteId);
        }
        else {
            dataItem.ticketingWebsiteId = undefined;
        }

        dataItem.venueKey = undefined;
        if (venues.length > 0) {
            const venue = venues.find(element => element.currentName === venueName);

            if (venue) {
                dataItem.venueKey = venue.venueKey;
            }
        }

        setPage(initialPageState);
        setSearchCriteria(dataItem);
        getMyShortedUnbroadcastedAvailableEvents(dataItem);
    }

    const getMyShortedUnbroadcastedAvailableEvents = (dataItem) => {
        setIsLoading(true);

        if (!dataItem) {
            dataItem = searchCriteria;
        }

        if (!dataItem) {
            dataItem = {};
        }

        fetch(`/api/administrator/pos/myshortedunbroadcastedavailableevents`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setMyShortedUnbroadcastedAvailableEvents(result);
                setTotalEventCount(result.length);
                setIsHeaderCheckboxChecked(false);
                setLastSelectedTicketGroupId(undefined);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const broadcastTicketGroups = (ticketGroupIds) => {
        if (window.confirm("Are you sure you want to broadcast these ticket groups?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            ticketGroupIds: ticketGroupIds,
        };

        fetch(`/api/administrator/pos/broadcastticketgroups`,
            {
                method: "PUT",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Events broadcasted successfully");

                setPage(initialPageState);
                getMyShortedUnbroadcastedAvailableEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const deleteTicketGroups = (ticketGroupIds) => {
        if (window.confirm("Are you sure you want to delete these ticket groups?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            ticketGroupIds: ticketGroupIds,
        };

        fetch(`/api/administrator/pos/deleteticketgroups`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Ticket groups deleted successfully");

                setPage(initialPageState);
                getMyShortedUnbroadcastedAvailableEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const deleteAndHideEvents = (ticketGroupIds, venueEventKeys) => {
        if (window.confirm("Are you sure you want to delete and permanently hide these events?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            ticketGroupIds: ticketGroupIds,
            venueEventKeys: venueEventKeys,
        };

        fetch(`/api/administrator/pos/deleteandhideticketgroups`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Events deleted and permanently hidden successfully");

                setPage(initialPageState);
                getMyShortedUnbroadcastedAvailableEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const getGridData = () => {
        return myShortedUnbroadcastedAvailableEvents.slice(page.skip, page.take + page.skip);
    };

    const viewStatuses = (eventStatuses) => {
        setStatuses(eventStatuses);
        setShowStatuses(true);
    };

    const viewSelectedUrls = () => {
        if (myShortedUnbroadcastedAvailableEvents.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No websites to view");
            return;
        }

        let count = 0;

        myShortedUnbroadcastedAvailableEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                count++;
                window.open(dataItem.ticketingUrl, "_blank", "noreferrer");
            }
        });

        if (count === 0) {
            addNotificationWithTimeout(dispatch, "error", "No websites to view");
            return;
        }
    };

    const ViewUrlHeaderCell = () => {
        return (
            <div>
                <Button type={"button"} fillMode="link" title="View Selected Websites" onClick={viewSelectedUrls} className="p-0">
                    View Selected
                </Button>
            </div>
        );
    };

    const ViewUrlCell = (props) => {
        const url = props.dataItem[props.field];

        return (
            <td className={"text-nowrap"}>
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const SelectedCell = (props) => {
        const ticketGroupId = props.dataItem.ticketGroupId;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, ticketGroupId, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const ViewStatusesCell = (props) => {
        const eventStatuses = props.dataItem.statuses;

        return (
            <td className={"text-nowrap"}>
                <Button
                    fillMode="link"
                    type={"button"}
                    onClick={() => viewStatuses(eventStatuses)}>View Statuses</Button>
            </td>
        );
    };

    const TagsCell = (props) => {
        const tags = props.dataItem.tags;

        return (
            <td className={"text-nowrap"}>
                <div className={"tag-list"}>
                    {tags.map((tag, index) => {
                        return <span className={"tag-list-item"}>{tag}</span>;
                    })}
                </div>
            </td>
        );
    };

    const selectedValueChanged = (event, ticketGroupId, selected) => {
        setIsLoading(true);

        setTimeout(() => {
            let myShortedUnbroadcastedAvailableEventsCopy = myShortedUnbroadcastedAvailableEvents;

            const itemIndex = myShortedUnbroadcastedAvailableEventsCopy.findIndex(dataItem =>
                dataItem.ticketGroupId === ticketGroupId
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedTicketGroupId !== undefined
                && lastSelectedTicketGroupId !== ticketGroupId) {
                const lastSelectedItemIndex = myShortedUnbroadcastedAvailableEventsCopy.findIndex(dataItem =>
                    dataItem.ticketGroupId === lastSelectedTicketGroupId
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        myShortedUnbroadcastedAvailableEventsCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                myShortedUnbroadcastedAvailableEventsCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedTicketGroupId(ticketGroupId);
            }

            setMyShortedUnbroadcastedAvailableEvents(myShortedUnbroadcastedAvailableEventsCopy);

            setIsHeaderCheckboxChecked(false);
            setIsLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setIsLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let myShortedUnbroadcastedAvailableEventsCopy = myShortedUnbroadcastedAvailableEvents;
            
            myShortedUnbroadcastedAvailableEventsCopy.forEach(dataItem =>
                dataItem.selected = false
            );

            if (checked === true) {
                const gridData = getGridData();

                myShortedUnbroadcastedAvailableEventsCopy.forEach(dataItem => {
                    const exists = gridData.includes(dataItem);

                    if (exists) {
                        dataItem.selected = true
                    }
                });
            }      

            setMyShortedUnbroadcastedAvailableEvents(myShortedUnbroadcastedAvailableEventsCopy);

            setLastSelectedTicketGroupId(undefined);

            setIsLoading(false);
        }, 1);
    };

    const getSelectedTicketGroupIds = () => {
        let ticketGroupIds = [];

        myShortedUnbroadcastedAvailableEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                ticketGroupIds.push(dataItem.ticketGroupId);
            }
        });

        return ticketGroupIds;
    }

    const getSelectedVenueEventKeys = () => {
        let venueEventKeys = [];

        myShortedUnbroadcastedAvailableEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                venueEventKeys.push(dataItem.venueEventKey);
            }
        });

        return venueEventKeys;
    }

    const broadcastSelectedTicketGroups = () => {
        let ticketGroupIds = getSelectedTicketGroupIds();

        if (ticketGroupIds.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No ticket groups selected");
            return;
        }

        broadcastTicketGroups(ticketGroupIds);
    };

    const deleteSelectedTicketGroups = () => {
        let ticketGroupIds = getSelectedTicketGroupIds();

        if (ticketGroupIds.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No ticket groups selected");
            return;
        }

        deleteTicketGroups(ticketGroupIds);
    };

    const deleteAndHideSelectedEvents = () => {
        let ticketGroupIds = getSelectedTicketGroupIds();
        let venueEventKeys = getSelectedVenueEventKeys();

        if (ticketGroupIds.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No ticket groups selected");
            return;
        }

        deleteAndHideEvents(ticketGroupIds, venueEventKeys);
    };

    const getTicketingWebsites = () => {
        const dataItem = {
            onlyActive: true,
        }

        fetch(`/api/general/ticketingwebsites`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                const allTicketingWebsites = [defaultTicketingWebsite].concat(result);
                setTicketingWebsites(allTicketingWebsites);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            });
    }

    const venueChanged = (event) => {
        const value = event.value;

        clearTimeout(autocompleteTimeout.current);

        if (!value || value.length < 2) {
            setVenues([]);
            setAreVenuesLoading(false);
            setVenueName(null);
            return;
        }

        autocompleteTimeout.current = setTimeout(() => {
            const dataItem = {
                name: value,
                onlyActive: true,
            };

            fetch(`/api/administrator/venues`,
                {
                    method: "POST",
                    headers: {
                        'Content-Type': 'application/json',
                        "X-XSRF-TOKEN": getCSRFToken(),
                    },
                    body: JSON.stringify(dataItem),
                })
                .then(handleResponse)
                .then(result => {
                    setVenues(result.venues);
                    setVenueName(value);
                })
                .catch(error => {
                    addNotificationWithTimeout(dispatch, "error", error.message);
                })
                .finally(() => {
                    setAreVenuesLoading(false);
                });
        }, timeoutDelay);

        setAreVenuesLoading(true);
    };

    useEffect(() => {
        document.title = "My Shorted Unbroadcasted Available Events";

        getTicketingWebsites();

        if (formReference.current) {
            formReference.current.onSubmit();
        }
    }, []);

    return (
        <div className="administratorReport">
            <Form
                onSubmit={getData}
                ignoreModified={true}
                ref={formReference}
                render={(formRenderProps) => (
                    <FormElement>
                        <fieldset className={"k-form-fieldset container"}>
                            <div className="row">
                                <div className="col-md-3">
                                    <Field
                                        id={"ticketingWebsite"}
                                        name={"ticketingWebsite"}
                                        label={"Ticketing website"}
                                        component={FormDropDownList}
                                        data={ticketingWebsites}
                                        textField="name"
                                        dataItemKey="ticketingWebsiteId"
                                        defaultValue={ticketingWebsites.find(element => element.ticketingWebsiteId === "")}
                                    />
                                </div>
                                <div className="col-md-7">
                                    <Field
                                        id={"venueName"}
                                        name={"venueName"}
                                        label={"Venue"}
                                        component={FormAutoComplete}
                                        data={venues}
                                        placeholder="e.g. Roseland"
                                        textField="currentName"
                                        onChange={venueChanged}
                                        loading={areVenuesLoading}
                                    />
                                </div>                                
                                <div className="col-md-2">
                                    <div className="k-form-field">
                                        <label className="k-label">&nbsp;</label>
                                        <Button
                                            type={"submit"}
                                            themeColor={"primary"}
                                            icon="search"
                                        >
                                            Search
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </fieldset>
                    </FormElement>
                )}
            />

            {isLoading ? <LoadingPanel /> : <></>}

            {!myShortedUnbroadcastedAvailableEvents ? <></> :
                <>
                    <div className="mb-3 ms-2 d-flex flex-row">
                        <div className="me-2">
                            <Button type={"button"} icon="eye" title="Broadcast Selected Ticket Groups" onClick={broadcastSelectedTicketGroups}>
                                Broadcast Selected Ticket Groups
                            </Button>
                        </div>
                        <div className="me-2">
                            <Button type={"button"} icon="trash" title="Delete Selected Ticket Groups" onClick={deleteSelectedTicketGroups}>
                                Delete Selected Ticket Groups
                            </Button>
                        </div>
                        <div>
                            <Button type={"button"} icon="trash" title="Delete and Permanently Hide Selected Events" onClick={deleteAndHideSelectedEvents}>
                                Delete and Permanently Hide Selected Events
                            </Button>
                        </div>
                    </div>

                    <Grid
                        data={getGridData()}
                        resizable={true}
                        skip={page.skip}
                        take={page.take}
                        total={totalEventCount}
                        pageable={true}
                        onPageChange={pageChange}
                        scrollable="none"
                    >
                        <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                        <Column field="ticketingUrl" cell={ViewUrlCell} headerCell={ViewUrlHeaderCell} />
                        <Column title={"Statuses"} cell={ViewStatusesCell} />
                        <Column field="ticketingWebsiteName" title={"Website"} />
                        <Column field="cost" title={"Cost"} />
                        <Column field="eventName" title={"Event"} />
                        <Column field="venueName" title={"Venue"} />
                        <Column field="eventDate" title={"Date"} />
                        <Column field="venueEventStatusTypeName" title={"Status"} />
                        <Column field="quantity" title={"Quantity"} />
                        <Column cell={TagsCell} title={"Tags"} />
                    </Grid>

                    {showStatuses === false ? <></> :
                        <Dialog
                            title="Statuses"
                            onClose={() => setShowStatuses(false)}
                            width={"90vw"}
                        >
                            <Grid
                                data={statuses}
                                resizable={true}
                                scrollable="none"
                            >
                                <Column field="dateCreated" title={"Date"} />
                                <Column field="oldVenueEventStatusTypeName" title={"Old Status"} />
                                <Column field="newVenueEventStatusTypeName" title={"New Status"} />
                            </Grid>
                        </Dialog>
                    }
                </>
            }
        </div>
    );
}